import { useNavigate } from 'react-router-dom';
import { admin, palette, radii, shadow, typography } from '../../../../styles/tokens';
import type { ImportBatchResult } from '../shared/types';

interface SuccessStepProps {
  result: ImportBatchResult;
  rowsWithoutContact: number;
  onImportAnother: () => void;
  onBackToSetupGuide: () => void;
}

export default function SuccessStep({
  result,
  rowsWithoutContact,
  onImportAnother,
  onBackToSetupGuide,
}: SuccessStepProps) {
  const navigate = useNavigate();
  const failed = result.failed.length;
  const nothingHappened =
    result.succeeded === 0 &&
    result.merged === 0 &&
    failed === 0 &&
    result.skipped > 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{
          padding: 24,
          background: palette.white,
          border: `1px solid ${palette.g150}`,
          borderRadius: radii.md,
          boxShadow: shadow.xs,
        }}
      >
        {nothingHappened && (
          <>
            <div
              style={{
                fontSize: typography.size.title,
                fontWeight: typography.weight.bold,
                color: admin.header,
                marginBottom: 8,
              }}
            >
              No changes made
            </div>
            <div
              style={{
                fontSize: typography.size.body,
                color: admin.body,
                lineHeight: typography.lineHeight.relaxed,
              }}
            >
              You skipped every row in the preview. No clients were added,
              merged, or updated.
            </div>
          </>
        )}

        {!nothingHappened && result.succeeded > 0 && (
          <div
            style={{
              fontSize: typography.size.title,
              fontWeight: typography.weight.bold,
              color: admin.header,
              marginBottom: 12,
            }}
          >
            {result.succeeded} client{result.succeeded === 1 ? '' : 's'} imported.
          </div>
        )}

        {!nothingHappened && result.merged > 0 && (
          <SummaryLine>
            {result.merged} merged into existing clients
            {result.mergedFields.length > 0
              ? ` (filled in ${formatList(result.mergedFields)})`
              : ''}
            .
          </SummaryLine>
        )}

        {!nothingHappened && rowsWithoutContact > 0 && (
          <SummaryLine>
            {rowsWithoutContact} have no contact info — add it from the
            Clients page anytime.
          </SummaryLine>
        )}

        {!nothingHappened && failed > 0 && (
          <div
            style={{
              marginTop: 12,
              padding: 12,
              background: palette.redWash,
              border: `1px solid ${palette.red}`,
              borderRadius: radii.sm,
              fontSize: typography.size.caption,
              color: palette.red,
            }}
          >
            {failed} row{failed === 1 ? '' : 's'} skipped due to errors. Try
            re-uploading just the failed rows, or reach out for help.
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <PrimaryButton
          onClick={() => navigate('/clients?source=import_spreadsheet')}
        >
          See them in Clients
        </PrimaryButton>
        <SecondaryButton onClick={onImportAnother}>
          Import another spreadsheet
        </SecondaryButton>
        <SecondaryButton onClick={onBackToSetupGuide}>
          Back to Setup Guide
        </SecondaryButton>
      </div>
    </div>
  );
}

function SummaryLine({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: typography.size.body,
        color: admin.body,
        marginBottom: 8,
        lineHeight: typography.lineHeight.relaxed,
      }}
    >
      {children}
    </div>
  );
}

function formatList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function PrimaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '10px 20px',
        background: palette.accent,
        color: palette.white,
        border: 'none',
        borderRadius: radii.sm,
        fontSize: typography.size.body,
        fontWeight: typography.weight.semibold,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function SecondaryButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '10px 20px',
        background: palette.white,
        color: admin.body,
        border: `1px solid ${palette.g200}`,
        borderRadius: radii.sm,
        fontSize: typography.size.body,
        fontWeight: typography.weight.semibold,
        fontFamily: 'inherit',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
